import { useState } from 'react';
import { FormControl, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFormContext } from 'react-hook-form';
import styles from './PropertyLocation.module.scss';
import {
  getDepthLabel,
  LocationAutocomplete,
} from '@/components/LocationAutocomplete/LocationAutocomplete';
import { useLocationAutocomplete } from '@/components/LocationAutocomplete';
import { LocationModel } from '@/types/location';
import { SectionTitle } from '@/components/uiComponents';

const OPFormHelperText = styled(FormHelperText)`
  margin: 0 0 5px 0;
  color: var(--primary-dark-blue, #2a3842);
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  span {
    color: var(--secondary, #f00);
  }
`;

const normalizeInitialLocationText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

interface PropertyLocationProps {
  addressEn?: string;
  setAddressEn: (value: string | undefined) => void;
  address?: string;
  setAddress: (value: string) => void;
  coordinatesLat?: number;
  setCoordinatesLat: (value: number | undefined) => void;
  coordinatesLong?: number;
  setCoordinatesLong: (value: number | undefined) => void;
}

export const PropertyLocation = ({
  addressEn = '',
  setAddressEn,
  setCoordinatesLat,
  setCoordinatesLong,
}: PropertyLocationProps) => {
  const { getValues, setValue } = useFormContext();
  const [initialInputAddressValue] = useState(
    () =>
      normalizeInitialLocationText(getValues('nameEn')) || normalizeInitialLocationText(addressEn)
  );
  const { inputAddressValue, setInputAddressValue, locations, setSelectedLocation } =
    useLocationAutocomplete({
      defaultValue: initialInputAddressValue,
    });

  const clearStructuredLocation = () => {
    setSelectedLocation(null);
    setValue('nameEn', undefined);
    setValue('placeId', undefined);
    setValue('coordinatesLat', undefined);
    setValue('locationId', undefined);
    setValue('coordinatesLong', undefined);
    setCoordinatesLat(undefined);
    setCoordinatesLong(undefined);
  };

  const updateLocationDetails = (location: LocationModel) => {
    const locationName = location[location.kind] ?? '';
    const fullAddress = [getDepthLabel(location), locationName].filter(Boolean).join(', ');

    setAddressEn(fullAddress);
    setSelectedLocation(location);
    setValue('addressEn', fullAddress);
    setValue('nameEn', locationName || null);
    setCoordinatesLat(location.latitude);
    setValue('coordinatesLat', String(location.latitude));
    setValue('locationId', String(location.id));
    setCoordinatesLong(location.longitude);
    setValue('coordinatesLong', String(location.longitude));
  };

  const handleInputChange = (value: string) => {
    setAddressEn(value);
    setValue('addressEn', value);
    clearStructuredLocation();
  };

  const handleClear = () => {
    setInputAddressValue('');
    setAddressEn(undefined);
    setValue('addressEn', undefined);
    clearStructuredLocation();
  };

  return (
    <div style={{ width: '100%' }}>
      <SectionTitle number={4} title='Choose location' />
      <FormControl sx={{ width: '100%' }}>
        <OPFormHelperText className={styles.label}>
          Enter location<span>*</span>
        </OPFormHelperText>
        <LocationAutocomplete
          locations={locations}
          inputAddressValue={inputAddressValue}
          setInputAddressValue={setInputAddressValue}
          onSelect={updateLocationDetails}
          onInputChange={handleInputChange}
          handleClear={handleClear}
        />
      </FormControl>
    </div>
  );
};
